import debounce from 'lodash/debounce';
import React, {
    forwardRef, useCallback, useEffect, useState, Ref,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import {
    AutoComplete, Icon, Loader, TextInput,
} from '../../../../../../ui-library';
import { ICON_TYPE_SEARCH } from '../../../../../../ui-library/components/Icon';
import { getAgentInfo } from '../../pages/SinglePortfolioView/pages/Details/utils';

import './PbNumber.css';

// --- Interfaces ---

interface FilteredPbNumber {
    label: string;
    value: string | number;
    name: string | null;
    vstNumber: string | null;
}

interface PbNumberProps {
    error?: string | null;
    onSelect: (value: string | number) => void;
    isManualPBRestricted?: boolean;
    value: string | number;
    setAgentInfo: (info: string | null | undefined) => void;
    label?: string;
    required?: boolean;
    includeVstNumber?: boolean;
    vstNumberValue?: string;
    onVstNumberChange?: (value: string) => void;
    vstNumberError?: string | null;
    innerRef?: Ref<HTMLInputElement>;
    // Allow for rest props passed to AutoComplete/TextInput
    [key: string]: any;
}

// --- Component ---

const PbNumber: React.FC<PbNumberProps> = ({
    error,
    onSelect,
    isManualPBRestricted = false,
    value,
    setAgentInfo,
    label,
    required,
    includeVstNumber,
    vstNumberValue,
    onVstNumberChange,
    vstNumberError,
    innerRef,
    ...rest
}) => {
    const { t } = useTranslation();
    const [pbNumbersFilter, setPBNumbersFilter] = useState<string>(value as string);
    const [filteredPbNumbers, setFilteredPbNumbers] = useState<FilteredPbNumber[]>([]);
    const [pbNotListed, setPbNotListed] = useState<boolean>(false);
    const [selectedPbNumber, setSelectedPbNumber] = useState<FilteredPbNumber | null>(null);
    const [hasFocus, setHasFocus] = useState<boolean>(false);

    const resetAgentInfo = () => {
        if (setAgentInfo) {
            setAgentInfo(null);
        }
    };

    const {
        data: pbNumbersData, isLoading: searchInProgress,
    } = useGetCustomService({
        service: 'customService',
        api: isManualPBRestricted ? 'searchRestrictedPbNumbers' : 'searchPbNumbers',
        payload: [{ filter: pbNumbersFilter }],
        enabled: !!pbNumbersFilter,
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const pbNumbers = useMemo(() => pbNumbersData?.data || [], [pbNumbersData?.data]);

    useEffect(() => {
        if (value && !pbNotListed) {
            setPBNumbersFilter(value as string);
            if (!isManualPBRestricted && !pbNumbers?.length && !searchInProgress) {
                setPbNotListed(true);
            }
        }
    }, [value, pbNotListed, isManualPBRestricted, pbNumbers, searchInProgress]);

    useEffect(() => {
        const pbNumbersFiltered: FilteredPbNumber[] = pbNumbers?.map(i => {
            const agentInfo = getAgentInfo(t, i?.agentName, i?.provisionReceiver);

            return {
                label: `${i.pbNumber} - ${agentInfo}`,
                value: i.pbNumber,
                name: agentInfo,
                vstNumber: i.vstNumber ? String(i.vstNumber) : null,
            };
        });

        const finalOptions = isManualPBRestricted
            ? pbNumbersFiltered
            : [
                ...pbNumbersFiltered,
                {
                    label: t('portfolio.createNewPortfolio.agentInformation.pbNotSet'),
                    value: -1,
                    name: null,
                    vstNumber: null,
                },
            ];

        setFilteredPbNumbers(finalOptions);
    }, [pbNumbers, isManualPBRestricted, t]);

    useEffect(() => {
        if (pbNotListed) {
            return;
        }

        const pbNumber = filteredPbNumbers.find(f => f.value === value);

        if (onVstNumberChange) {
            onVstNumberChange(pbNumber ? (pbNumber.vstNumber || '') : (vstNumberValue || ''));
        }

        setAgentInfo(pbNumber?.name);
        setSelectedPbNumber(pbNumber || null);
    }, [filteredPbNumbers, value, pbNotListed, onVstNumberChange, setAgentInfo, vstNumberValue]);

    const searchPbNumbers = useCallback(
        debounce(async (val: string) => {
            if (!val) {
                onSelect('');
                resetAgentInfo();
            }

            setPBNumbersFilter(val || '');
        }, 200),
        [isManualPBRestricted, onSelect],
    );

    const onSelectItem = (val: string | number) => {
        if (Number(val) > -1) {
            setPbNotListed(false);
            onSelect(val);

            return;
        }

        setSelectedPbNumber(null);
        onSelect('');
        if (!isManualPBRestricted) setPbNotListed(true);
        resetAgentInfo();
        if (onVstNumberChange) {
            onVstNumberChange('');
        }
    };

    const onCustomPbNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelect(e.target.value);
    };

    return (
        <>
            <div>
                <AutoComplete
                    {...rest}
                    value={!pbNotListed ? value as string : ''}
                    type="text"
                    onSelect={onSelectItem}
                    className="pb-number"
                    dropdownClassName="search-dropdown"
                    dataSource={filteredPbNumbers}
                    onSearch={searchPbNumbers}
                    externalSearch
                    isDropdownOpen={hasFocus}
                    placeholder={t('shadowAccount.depot.searchForPbNumber')}
                >
                    <TextInput
                        ref={innerRef}
                        data-cy="pbNumber"
                        label={label}
                        required={required}
                        id="pbNumber"
                        onFocus={() => setHasFocus(true)}
                        onBlur={() => setHasFocus(false)}
                        error={!pbNotListed ? error : null}
                        prefix={searchInProgress ? <Loader /> : <Icon type={ICON_TYPE_SEARCH} className="search-icon" />}
                    />
                </AutoComplete>
                {pbNotListed && (
                    <TextInput
                        data-cy="pbNumberCustom"
                        type="number"
                        className="pb-number custom"
                        value={value}
                        error={error}
                        onChange={onCustomPbNumberChange}
                    />
                )}
            </div>
            {(includeVstNumber && pbNotListed) && (
                <>
                    <span className="agent-name">{selectedPbNumber?.name}</span>
                    <TextInput
                        data-cy="vstNumber"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onVstNumberChange?.(e.target.value)}
                        value={vstNumberValue}
                        error={vstNumberError}
                        label={t('portfolio.createNewPortfolio.agentInformation.VSTNumber')}
                        required
                    />
                </>
            )}
        </>
    );
};

export default forwardRef<HTMLInputElement, Omit<PbNumberProps, 'innerRef'>>((props, ref) => (
    <PbNumber {...props} innerRef={ref} />
));
